import {
  SadieSimulatorLineDiff,
  SadieSimulatorPromptChange,
  SadieSimulatorPromptDiffSection,
  SadieSimulatorPromptSection,
} from "@/types";

// Where an added section goes when the one it follows isn't there. Mirrors
// FALLBACK_BEFORE in mastra's prompt-changes.ts.
const FALLBACK_BEFORE = "WHO YOU ARE AND HOW YOU TEXT";

const normalizeHeading = (heading: string) =>
  heading
    .trim()
    .replace(/^#+\s*/, "")
    .toUpperCase();

export const sadieSimulator = {
  // What a change does: with `after` it adds a section, with empty text it
  // removes one, and otherwise it changes one.
  changeKind: (
    change: SadieSimulatorPromptChange,
  ): "ADD" | "REMOVE" | "CHANGE" => {
    if (change.after !== undefined) return "ADD";
    return change.text.trim() ? "CHANGE" : "REMOVE";
  },

  // The section with this heading, whatever its case.
  sectionFor: (
    sections: SadieSimulatorPromptSection[],
    heading: string,
  ): SadieSimulatorPromptSection | undefined =>
    sections.find(
      (s) => normalizeHeading(s.heading) === normalizeHeading(heading),
    ),

  // The sections with the changes applied in order, the way mastra applies
  // them to her next turn, so the editor shows what that turn will get.
  // Mirrors applyPromptChanges in mastra's prompt-changes.ts.
  withPromptChanges: (
    sections: SadieSimulatorPromptSection[],
    changes: SadieSimulatorPromptChange[],
  ): SadieSimulatorPromptSection[] => {
    const result = [...sections];
    const indexOf = (heading: string) =>
      result.findIndex(
        (s) => normalizeHeading(s.heading) === normalizeHeading(heading),
      );

    for (const change of changes) {
      const at = indexOf(change.heading);
      if (change.after !== undefined) {
        if (at !== -1) continue;
        const after = indexOf(change.after);
        const added = {
          heading: normalizeHeading(change.heading),
          text: change.text.trim(),
        };
        if (after !== -1) {
          result.splice(after + 1, 0, added);
          continue;
        }
        const before = indexOf(FALLBACK_BEFORE);
        result.splice(before === -1 ? result.length : before, 0, added);
        continue;
      }
      if (at === -1) continue;
      if (!change.text.trim()) {
        result.splice(at, 1);
        continue;
      }
      result[at] = { heading: result[at].heading, text: change.text.trim() };
    }
    return result;
  },

  // The whole prompt from its sections, joined the way mastra joins them.
  promptText: (sections: SadieSimulatorPromptSection[]): string =>
    sections.map((s) => `## ${s.heading}\n${s.text}`).join("\n\n"),

  // Every section with the changes applied, in order, each marked by how the
  // changes left it. A removed section stays just before the section that
  // followed it.
  promptDiff: (
    sections: SadieSimulatorPromptSection[],
    changes: SadieSimulatorPromptChange[],
  ): SadieSimulatorPromptDiffSection[] => {
    const edited = sadieSimulator.withPromptChanges(sections, changes);
    const built = new Map(
      sections.map((s) => [normalizeHeading(s.heading), s]),
    );
    const kept = new Set(edited.map((s) => normalizeHeading(s.heading)));

    // Removed sections, by the kept one that came next. Null is the end.
    const removedBefore = new Map<
      string | null,
      SadieSimulatorPromptDiffSection[]
    >();
    let next: string | null = null;
    for (const section of [...sections].reverse()) {
      const key = normalizeHeading(section.heading);
      if (kept.has(key)) {
        next = key;
        continue;
      }
      const removed: SadieSimulatorPromptDiffSection = {
        heading: section.heading,
        text: section.text,
        status: "REMOVED",
        before: section.text,
      };
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      removedBefore.set(next, [removed, ...(removedBefore.get(next) || [])]);
    }
    const removedAt = (key: string | null) =>
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      removedBefore.get(key) || [];
    const statusOf = (
      section: SadieSimulatorPromptSection,
      original: SadieSimulatorPromptSection | undefined,
    ): SadieSimulatorPromptDiffSection["status"] => {
      if (!original) return "ADDED";
      return original.text === section.text ? "SAME" : "CHANGED";
    };

    return [
      ...edited.flatMap((section) => {
        const key = normalizeHeading(section.heading);
        const original = built.get(key);
        return [
          ...(original ? removedAt(key) : []),
          {
            heading: section.heading,
            text: section.text,
            status: statusOf(section, original),
            before: original ? original.text : null,
          },
        ];
      }),
      ...removedAt(null),
    ];
  },

  // A changed section's lines against the same section as built: kept,
  // added or removed, by longest common subsequence.
  lineDiff: (before: string, after: string): SadieSimulatorLineDiff[] => {
    const a = before.split("\n");
    const b = after.split("\n");
    const common = Array.from({ length: a.length + 1 }, () =>
      new Array<number>(b.length + 1).fill(0),
    );
    for (let i = a.length - 1; i >= 0; i--) {
      for (let j = b.length - 1; j >= 0; j--) {
        common[i][j] =
          a[i] === b[j]
            ? common[i + 1][j + 1] + 1
            : Math.max(common[i + 1][j], common[i][j + 1]);
      }
    }
    const lines: SadieSimulatorLineDiff[] = [];
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) {
        lines.push({ kind: "SAME", text: a[i] });
        i++;
        j++;
      } else if (common[i + 1][j] >= common[i][j + 1]) {
        lines.push({ kind: "REMOVED", text: a[i] });
        i++;
      } else {
        lines.push({ kind: "ADDED", text: b[j] });
        j++;
      }
    }
    for (; i < a.length; i++) lines.push({ kind: "REMOVED", text: a[i] });
    for (; j < b.length; j++) lines.push({ kind: "ADDED", text: b[j] });
    return lines;
  },
};
